import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SortAsc, Plus, X } from "lucide-react";
import { toast } from "sonner";

interface SortCondition {
  id: string;
  field: string;
  direction: "asc" | "desc";
}

interface SortBuilderProps {
  fields: any[];
  onApplySort: (sorts: SortCondition[]) => void;
  currentSort: SortCondition[];
}

export const SortBuilder = ({ fields, onApplySort, currentSort }: SortBuilderProps) => {
  const [open, setOpen] = useState(false);
  const [sorts, setSorts] = useState<SortCondition[]>(currentSort);

  const addSort = () => {
    setSorts([
      ...sorts,
      {
        id: Math.random().toString(),
        field: fields[0]?.name || "",
        direction: "asc",
      },
    ]);
  };

  const removeSort = (id: string) => {
    setSorts(sorts.filter((s) => s.id !== id));
  };

  const updateSort = (id: string, key: keyof SortCondition, value: string) => {
    setSorts(sorts.map((s) => (s.id === id ? { ...s, [key]: value } : s)));
  };

  const handleApply = () => {
    onApplySort(sorts);
    setOpen(false);
    toast.success("Sorting applied");
  };

  const handleClear = () => {
    setSorts([]);
    onApplySort([]);
    toast.success("Sorting cleared");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <SortAsc className="h-4 w-4 mr-2" />
          Sort {currentSort.length > 0 && `(${currentSort.length})`}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Sort Records</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {sorts.map((sort, index) => (
            <div key={sort.id} className="flex gap-2 items-center">
              <span className="text-sm text-muted-foreground w-8">{index + 1}.</span>
              <Select
                value={sort.field}
                onValueChange={(value) => updateSort(sort.id, "field", value)}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {fields.map((field) => (
                    <SelectItem key={field.id} value={field.name}>
                      {field.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={sort.direction}
                onValueChange={(value) => updateSort(sort.id, "direction", value)}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">A → Z</SelectItem>
                  <SelectItem value="desc">Z → A</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => removeSort(sort.id)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button onClick={addSort} variant="outline" className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Sort Level
          </Button>
        </div>
        <div className="flex gap-2 justify-end pt-4">
          <Button variant="outline" onClick={handleClear}>
            Clear All
          </Button>
          <Button onClick={handleApply}>Apply Sort</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
